import LazyImage from "../../common/LazyImage";
import logo from "../../assets/logo.png";

export default function ProductDetailLayout({ title, subcategory }) {
  if (!subcategory) return null;

  const { subTitle, description, specs, features, images, variants } =
    subcategory;

  const ImageBox = ({ src, alt }) => (
    <div className="relative h-52 w-full overflow-hidden bg-neutral-800 flex items-center justify-center rounded-lg border border-white/10">
      {src ? (
        <>
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </>
      ) : (
        <img src={logo} alt={alt} className="h-16" />
      )}
    </div>
  );

  return (
    <div className="text-white py-8 md:py-10 animate-[page-in_.35s_ease]  bg-transparent relative scroll-mt-24">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-wide">
            {title}
          </h1>
          {subTitle && (
            <p className="mt-2 text-sm uppercase tracking-widest text-white/70">
              {subTitle}
            </p>
          )}
        </div>

        <div className="mt-6 md:mt-0 md:text-right">
          <h2 className="text-lg tracking-widest uppercase text-white/70">
            Technical Specification
          </h2>
          <div className="w-24 h-[1px] bg-white/30 mt-2 ml-auto" />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 mb-16">
        <div>
          <p className="text-white/80 leading-relaxed mb-8 whitespace-pre-line">
            {description}
          </p>

          {features && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mb-12">
              {features.map((f, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center text-center space-y-2"
                >
                  <img
                    src={f.icon}
                    alt={f.label}
                    className="w-10 h-10 opacity-90 bg-white/90 rounded"
                  />
                  <span className="text-xs text-white/70">{f.label}</span>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {images && images.length > 0 ? (
              images.map((img, i) => <ImageBox key={i} src={img} alt={title} />)
            ) : (
              <>
                <ImageBox src={""} alt={title} />
                <ImageBox src={""} alt={title} />
              </>
            )}
          </div>
        </div>

        <div>
          {specs && (
            <div className="bg-neutral-900/60 p-6 rounded-xl border border-white/10 mb-6">
              <ul className="space-y-1 text-sm text-white/80">
                {Object.entries(specs).map(([k, v]) => (
                  <li key={k}>
                    <span className="text-white/60">{k}:</span> {v}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {variants && (
        <div className="space-y-20">
          {Object.entries(variants).map(([key, variant]) => (
            <div key={key} className="border-t border-white/10 pt-10">
              <h2 className="text-2xl font-semibold mb-2">
                {variant.title || `${title} - ${key}`}
              </h2>
              {variant.subTitle && (
                <p className="text-sm uppercase text-white/60 mb-4">
                  {variant.subTitle}
                </p>
              )}

              <div className="grid lg:grid-cols-2 gap-10">
                <div>
                  <p className="text-white/80 leading-relaxed mb-8 whitespace-pre-line">
                    {variant.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {variant.images && variant.images.length > 0 ? (
                      variant.images.map((img, i) => (
                        <ImageBox key={i} src={img} alt={variant.title} />
                      ))
                    ) : (
                      <>
                        <ImageBox src={""} alt={variant.title} />
                        <ImageBox src={""} alt={variant.title} />
                      </>
                    )}
                  </div>
                </div>

                <div>
                  {variant.specs && (
                    <div className="bg-neutral-900/60 p-6 rounded-xl border border-white/10">
                      <h3 className="text-white/70 mb-3 text-sm uppercase tracking-wide">
                        {key} Specifications
                      </h3>
                      <ul className="space-y-1 text-sm text-white/80">
                        {Object.entries(variant.specs).map(([k, v]) => (
                          <li key={k}>
                            <span className="text-white/60">{k}:</span> {v}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
